import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Paper, 
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  DirectionsCar,
  Person,
  Assessment,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import TruckList from '../components/TruckList';
import LiveFleetMap from '../components/LiveFleetMap';
import VehicleDetailsPanel from '../components/VehicleDetailsPanel';
import DriverManagement from '../components/DriverManagement';
import VehicleStatsChart from '../components/charts/VehicleStatsChart';
import samsaraPollingService from '../services/samsaraPollingService';
import apiClient from '../api';
import { mapSamsaraVehicleType } from '../utils/samsaraVehicleType';
import { useAuth } from '../context/AuthContext';

const TrucksListPage = () => {
  const { user } = useAuth();
  const canQuerySamsara = user?.role === 'admin' || user?.role === 'company_admin';
  const [activeTab, setActiveTab] = useState(0);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [samsaraConnected, setSamsaraConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [samsaraVehicles, setSamsaraVehicles] = useState([]);
  const [fleetOverview, setFleetOverview] = useState({
    totalVehicles: 0,
    activeVehicles: 0,
    maintenanceVehicles: 0,
    inactiveVehicles: 0,
    realTimeLocations: 0,
    vehiclesByType: { vans: 0, flatbeds: 0, trailers: 0 }
  });

  const mountedRef = useRef(false);
  const vehiclesRefreshIntervalRef = useRef(null);
  
  useEffect(() => {
    if (!canQuerySamsara) {
      setSamsaraConnected(false);
      setLoading(false);
      return undefined;
    }
    if (mountedRef.current) return undefined;
    mountedRef.current = true;
    checkSamsaraConnection();

    return () => {
      if (vehiclesRefreshIntervalRef.current) {
        clearInterval(vehiclesRefreshIntervalRef.current);
      }
    };
  }, [canQuerySamsara]);

  // Removed automatic 5-minute refresh - user can manually refresh via button

  const checkSamsaraConnection = async () => {
    try {
      const isConnected = await samsaraPollingService.checkConnection();
      setSamsaraConnected(isConnected);
      
      if (isConnected) {
        await fetchSamsaraVehicles();
      }
    } catch (error) {
      console.warn('Failed to check Samsara connection:', error);
      setSamsaraConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchSamsaraVehicles = async () => {
    try {
      // Fetch all data in parallel for faster loading
      const [locationsResponse, vehiclesResponse, assetsResponse] = await Promise.allSettled([
        apiClient.get('/samsara/vehicles/locations', {
          params: { limit: 100, onlyWithRecentTrips: true }
        }),
        apiClient.get('/samsara/vehicles', {
          params: { onlyWithRecentTrips: true }
        }),
        apiClient.get('/samsara/vehicles/assets', {
          params: { onlyWithRecentTrips: true }
        })
      ]);
      
      // Extract data from responses
      let vehiclesWithLocations = [];
      if (locationsResponse.status === 'fulfilled') {
        vehiclesWithLocations = locationsResponse.value.data.data || [];
      }
      
      let vehicleDetails = [];
      if (vehiclesResponse.status === 'fulfilled') {
        vehicleDetails = vehiclesResponse.value.data.data || [];
      }
      
      let vehicleAssets = [];
      if (assetsResponse.status === 'fulfilled') {
        vehicleAssets = assetsResponse.value.data.data || [];
      }
      
      // Fallback: if any fetch returned 0 results, try without filter (in parallel)
      const needsFallback = vehiclesWithLocations.length === 0 || vehicleDetails.length === 0 || vehicleAssets.length === 0;
      if (needsFallback) {
        const fallbackPromises = [];
        if (vehiclesWithLocations.length === 0) {
          fallbackPromises.push(
            apiClient.get('/samsara/vehicles/locations', { params: { onlyWithRecentTrips: false } })
              .then(res => { vehiclesWithLocations = res.data.data || []; })
              .catch(() => {})
          );
        }
        if (vehicleDetails.length === 0) {
          fallbackPromises.push(
            apiClient.get('/samsara/vehicles', { params: { onlyWithRecentTrips: false } })
              .then(res => { vehicleDetails = res.data.data || []; })
              .catch(() => {})
          );
        }
        if (vehicleAssets.length === 0) {
          fallbackPromises.push(
            apiClient.get('/samsara/vehicles/assets', { params: { onlyWithRecentTrips: false } })
              .then(res => { vehicleAssets = res.data.data || []; })
              .catch(() => {})
          );
        }
        await Promise.all(fallbackPromises);
      }
      
      // Create a map of vehicle details by ID (basic info)
      const detailsMap = new Map();
      vehicleDetails.forEach(vehicle => {
        const vehicleId = String(vehicle.id || '');
        if (vehicleId) {
          detailsMap.set(vehicleId, vehicle);
        }
      });
      
      // Create a map of vehicle assets by ID (VIN, make, model, year)
      const assetsMap = new Map();
      vehicleAssets.forEach(asset => {
        // Assets can have id, assetId, or vehicleId depending on structure
        const assetId = String(asset.id || asset.assetId || asset.vehicleId || asset.vehicle?.id || '');
        if (assetId) {
          assetsMap.set(assetId, asset);
        }
      });
      
      
      // Create a set of vehicle IDs that have location data
      const locationVehicleIds = new Set(
        vehiclesWithLocations.map(v => String(v.id || v.vehicleId || ''))
      );
      
      // Merge: Start with location data (has real-time status), merge in details and assets
      const mergedVehicles = vehiclesWithLocations.map(locationVehicle => {
        const vehicleId = String(locationVehicle.id || locationVehicle.vehicleId || '');
        const details = detailsMap.get(vehicleId);
        const asset = assetsMap.get(vehicleId);
        
        // Location data has: id, name, location, engineState, lastSeen
        // Details has: id, name
        // Assets has: id, vin, make, model, year (may be nested in vehicle object)
        const assetData = asset?.vehicle || asset;
        return {
          id: locationVehicle.id || locationVehicle.vehicleId,
          name: locationVehicle.name || details?.name || asset?.name || assetData?.name,
          vin: assetData?.vin || asset?.vin || assetData?.vehicleVin || asset?.vehicleVin,
          make: assetData?.make || asset?.make || assetData?.vehicleMake || asset?.vehicleMake,
          model: assetData?.model || asset?.model || assetData?.vehicleModel || asset?.vehicleModel,
          year: assetData?.year || asset?.year || assetData?.vehicleYear || asset?.vehicleYear,
          engineState: locationVehicle.engineState,
          location: locationVehicle.location,
          lastSeen: locationVehicle.lastSeen || locationVehicle.time
        };
      });
      
      // Add vehicles from details that don't have location data
      vehicleDetails.forEach(vehicle => {
        const vehicleId = String(vehicle.id || '');
        if (!locationVehicleIds.has(vehicleId)) {
          const asset = assetsMap.get(vehicleId);
          const assetData = asset?.vehicle || asset;
          mergedVehicles.push({
            id: vehicle.id,
            name: vehicle.name || asset?.name || assetData?.name,
            vin: assetData?.vin || asset?.vin || assetData?.vehicleVin || asset?.vehicleVin,
            make: assetData?.make || asset?.make || assetData?.vehicleMake || asset?.vehicleMake,
            model: assetData?.model || asset?.model || assetData?.vehicleModel || asset?.vehicleModel,
            year: assetData?.year || asset?.year || assetData?.vehicleYear || asset?.vehicleYear,
            engineState: null, // No real-time status available
            location: null,
            lastSeen: null
          });
        }
      });
      
      
      
      setSamsaraVehicles(mergedVehicles);
      calculateFleetOverview(mergedVehicles);
    } catch (error) {
      console.warn('[TrucksListPage] Failed to fetch Samsara vehicles:', error.message);
      // Try to fetch without filter as fallback
      try {
        const fallbackResponse = await apiClient.get('/samsara/vehicles', {
          params: { onlyWithRecentTrips: false }
        });
        const fallbackVehicles = fallbackResponse.data.data || [];
        setSamsaraVehicles(fallbackVehicles);
        calculateFleetOverview(fallbackVehicles);
      } catch (fallbackError) {
        console.error('[TrucksListPage] Failed to fetch vehicles even without filter:', fallbackError.message);
      }
    }
  };

  const calculateFleetOverview = (vehicles) => {
    const overview = {
      totalVehicles: vehicles.length,
      activeVehicles: 0,
      maintenanceVehicles: 0,
      inactiveVehicles: 0,
      realTimeLocations: 0,
      vehiclesByType: { vans: 0, flatbeds: 0, trailers: 0 }
    };

    vehicles.forEach(vehicle => {
      // Count by status
      // Note: /vehicles endpoint doesn't include engineState, only /vehicles/locations does
      // For now, assume vehicles are Active unless we have location data with engineState
      let status = 'Active'; // Default to Active
      
      // If we have engineState from location data, use it
      if (vehicle.engineState) {
        status = vehicle.engineState === 'ON' ? 'Active' : 
                vehicle.engineState === 'OFF' ? 'Inactive' : 'Maintenance';
      } else if (vehicle.status) {
        // Use status field if available
        status = vehicle.status;
      }
      
      if (status === 'Active') overview.activeVehicles++;
      else if (status === 'Maintenance') overview.maintenanceVehicles++;
      else overview.inactiveVehicles++;

      // Count real-time locations
      if (vehicle.location && vehicle.location.latitude && vehicle.location.longitude) {
        overview.realTimeLocations++;
      }

      // Count by vehicle type (map Samsara vehicle types to our system)
      const vehicleType = mapSamsaraVehicleType(vehicle);
      if (vehicleType === 'Van') overview.vehiclesByType.vans++;
      else if (vehicleType === 'Flatbed') overview.vehiclesByType.flatbeds++;
      else if (vehicleType === 'Trailer') overview.vehiclesByType.trailers++;
    });

    setFleetOverview(overview);
  };

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleVehicleSelect = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
    setActiveTab(1); // Switch to vehicle details tab
  }, []);

  const handleDriverSelect = useCallback((driver) => {
    setSelectedDriver(driver);
    setActiveTab(2); // Switch to driver management tab
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {samsaraConnected ? 'Fleet Management' : 'Truck Management'}
        </Typography>
        {samsaraConnected && selectedVehicle && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Selected Vehicle: {selectedVehicle.name} ({selectedVehicle.status})
          </Alert>
        )}
      </Paper>


      {/* Navigation Tabs - Only show when Samsara is connected */}
      {samsaraConnected && (
        <Paper sx={{ mb: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab 
              label="Live Fleet Map" 
              icon={<DirectionsCar />}
            />
            <Tab 
              label="Vehicle Details" 
              icon={<Assessment />}
              disabled={!selectedVehicle}
            />
            <Tab 
              label="Driver Management" 
              icon={<Person />}
            />
          </Tabs>
        </Paper>
      )}

      {/* Fleet Overview - Only show when Samsara is connected */}
      {samsaraConnected && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Fleet Overview ({fleetOverview.totalVehicles})
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {fleetOverview.totalVehicles}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Vehicles
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="success.main">
                    {fleetOverview.activeVehicles}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="warning.main">
                    {fleetOverview.maintenanceVehicles}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Maintenance
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="error.main">
                    {fleetOverview.inactiveVehicles}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Inactive
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Vehicle Type Breakdown */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Vehicle Types
            </Typography>
            <Grid container spacing={1}>
              <Grid item>
                <Chip 
                  label={`Vans: ${fleetOverview.vehiclesByType.vans}`}
                  color="primary"
                  variant="outlined"
                />
              </Grid>
              <Grid item>
                <Chip 
                  label={`Flatbeds: ${fleetOverview.vehiclesByType.flatbeds}`}
                  color="secondary"
                  variant="outlined"
                />
              </Grid>
              <Grid item>
                <Chip 
                  label={`Trailers: ${fleetOverview.vehiclesByType.trailers}`}
                  color="default"
                  variant="outlined"
                />
              </Grid>
              <Grid item>
                <Chip 
                  label={`Real-time: ${fleetOverview.realTimeLocations}`}
                  color="success"
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      {/* Content Area */}
      {samsaraConnected ? (
        <>
          {activeTab === 0 && (
            <>
              <LiveFleetMap onVehicleSelect={handleVehicleSelect} />
              <Box sx={{ mt: 2 }}>
                <Paper sx={{ p: 1, mb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Vehicle List</Typography>
                    <Tooltip title="Refresh vehicle list">
                      <IconButton 
                        onClick={() => {
                          fetchSamsaraVehicles();
                        }}
                        color="primary"
                        size="small"
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
                <TruckList 
                  onVehicleSelect={handleVehicleSelect}
                  samsaraVehicles={samsaraVehicles}
                  samsaraConnected={samsaraConnected}
                />
              </Box>
            </>
          )}

          {activeTab === 1 && selectedVehicle && (
            <Box>
              <VehicleDetailsPanel 
                vehicleId={selectedVehicle.id}
                vehicleData={selectedVehicle}
                onClose={() => setSelectedVehicle(null)}
              />
              <Box mt={2}>
                <VehicleStatsChart 
                  vehicleId={selectedVehicle.id}
                  vehicleName={selectedVehicle.name}
                />
              </Box>
            </Box>
          )}

          {activeTab === 2 && (
            <DriverManagement onDriverSelect={handleDriverSelect} />
          )}
        </>
      ) : (
        /* Truck list when Samsara is disconnected */
        <TruckList />
      )}
    </Box>
  );
};

export default TrucksListPage; 